import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import SizeCharts from "./graph/SizeCharts";

function mapRows(apiData = []) {
  return apiData.map((row) => ({
    size_name: row.size_name,
    item_name: row.item_name,
    qty: row.qty,
    tagPrice: row.tag_price,
    sellingPrice: row.selling_price,
    qty_sum_pct: row.qty_sum_pct,
    amount_sum_pct: row.amount_sum_pct,
  }));
}

function mapTotals(apiTotals = {}, hasData = false) {
  return {
    qty: apiTotals.qty,
    tagPrice: apiTotals.tag_price,
    sellingPrice: apiTotals.selling_price,
    qty_sum: apiTotals.qty_sum,
    amount_sum: apiTotals.amount_sum,
    qty_sum_pct: hasData ? 100 : 0,
    amount_sum_pct: hasData ? 100 : 0,
  };
}

function mapMetrics(summary = {}) {
  return [
    { label: "Size", value: formatNum(summary.size) },
    { label: "Qty", value: formatNum(summary.qty) },
    {
      label: "Top Selling Size (by Amount)",
      value: summary.top_selling_size_by_amount ?? "-",
    },
    {
      label: "Top Selling Size (by Qty)",
      value: summary.top_selling_size_by_qty ?? "-",
    },
  ];
}

const SIZE_COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  { key: "size_name", label: "Size", align: "left", width: 150 },
  { key: "item_name", label: "Item", align: "left", width: 180 },
  { key: "qty", label: "Qty", align: "right", width: 120 },
  { key: "tagPrice", label: "Tag Price", align: "right", width: 170 },
  { key: "sellingPrice", label: "Selling Price", align: "right", width: 170 },
  { key: "qty_sum_pct", label: "Qty% from over all sales", align: "right", width: 220 },
  { key: "amount_sum_pct", label: "Amount% from over all sales", align: "right", width: 250 },
];

const DEFAULT_PAGINATION = {
  total: 0,
  page: 1,
  limit: 10,
  total_pages: 1,
  has_next: false,
  has_prev: false,
};
const CHART_MAX_LIMIT = 100000;

const Size = () => {
  const [rows, setRows] = useState([]);
  const [chartRows, setChartRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showCharts, setShowCharts] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);

  const lastQueryRef = useRef({});
  const isFirstFetchRef = useRef(true);

  const fetchSalesBySize = (params = {}) => {
    const merged = { ...lastQueryRef.current, ...params };
    lastQueryRef.current = merged;

    if (isFirstFetchRef.current) setLoading(true);

    Apiauth(merged, "Inventory/reports/salesBySize")
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setTotals(mapTotals(res.totals, Array.isArray(res.data) && res.data.length > 0));
          setMetrics(mapMetrics(res.summary));
          setFilters(res.filters ?? {});
          setPagination(res.pagination ?? DEFAULT_PAGINATION);
        }
      })
      .finally(() => {
        setLoading(false);
        isFirstFetchRef.current = false;
      });
  };

  const fetchChartReport = (activeFilters = {}) => {
    setLoading(true);
    Apiauth(
      { page: 1, limit: CHART_MAX_LIMIT, ...activeFilters },
      "Inventory/reports/salesBySize",
    )
      .then((res) => {
        if (res?.code === 200) {
          setChartRows(mapRows(res.data));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSalesBySize({ page: 1, limit: 10 });
  }, []);

  useEffect(() => {
    if (showCharts) {
      fetchChartReport(appliedFilters);
    }
  }, [showCharts, appliedFilters]);

  return (
    <>
      <Header title="Sales by Size" metrics={metrics} />
      <SubHeader
        filters={filters}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={(query) => {
          setAppliedFilters(query);
          fetchSalesBySize({
            ...query,
            page: 1,
            limit: pagination.limit || 10,
          });
        }}
      />
      {loading ? (
        <TableLoader />
      ) : showCharts ? (
        <SizeCharts rows={chartRows} />
      ) : (
        <DataTable
          columns={SIZE_COLUMNS}
          data={rows}
          totals={totals}
          fetchData={fetchSalesBySize}
          pageLimit={pagination.limit}
          totalRecords={pagination.total}
          currentPage={pagination.page}
        />
      )}
    </>
  );
};

export default Size;